import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Divider,
  Link as MuiLink,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Checkbox,
  debounce,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import DragAndDropUpload from './UploadMod'
import 'src/components/styles/ListPage.css'
import './CatalogItemList.css'
import { Expandable } from '@impres-development/assets/client'
import ErrorScreen from 'src/Misc/ErrorScreen'
import { Check, Clear, Close, FileDownloadOutlined, PendingOutlined } from '@mui/icons-material'
import { Dayjs } from 'dayjs'
import { Link as RouterLink } from 'react-router-dom'
import { catalogColumnType, catalogItemType, catalogTabType } from './catalogTypes'
import { ComplianceStatus, sewpTrHeaderType } from 'src/types'
import { EditableCell } from 'src/EditQuote/steps/draft/EditableCell'
import BulkEditActionBar from './CatalogTypes/BulkEditActionBar'
import SewpTrOverlay from './CatalogTypes/SewpTrOverlay'
import { catalogTableItemListType } from './useCatalogData'
import BulkEdit from './CatalogTypes/BulkEdit'

export default function CatalogItemList({
  contractNumber,
  catalogItems,
  updateProperty,
  columnMapping,
  tabGroups,
  onTabChange,
  total,
  loading,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  reload,
  isModification,
  manufacturerFilter,
  onManufacturerFilterChange,
  mfrPartNumberFilter,
  onMfrPartNumberFilterChange,
}: {
  header: string
  contractNumber: string
  catalogItems: catalogTableItemListType
  updateProperty: (ids: string[], property: string, value: string) => Promise<void>
  columnMapping: Map<string, catalogColumnType[]>
  tabGroups: Map<string, catalogTabType[]>
  onTabChange: (tab: catalogTabType) => void
  total: number
  loading: 'loading' | 'error' | ''
  page: number
  rowsPerPage: number
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
  reload: () => Promise<void>
  isModification: boolean
  manufacturerFilter: string | null
  onManufacturerFilterChange: (value: string | null) => void
  mfrPartNumberFilter: string
  onMfrPartNumberFilterChange: (value: string) => void
}) {
  const [tabGroup, setTabGroup] = useState(tabGroups.get(contractNumber) ?? [])
  const [contractColumns, setContractColumns] = useState(columnMapping.get(contractNumber) ?? [])
  const [defaultVisible, setDefaultVisible] = useState(contractColumns.filter(c => !c.defaultHidden).map(c => c.key))
  const [visibleKeys, setVisibleKeys] = useState<string[]>(defaultVisible as string[])
  const [tableView, setTableView] = useState(tabGroup[0] ?? '')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [approveDenyState, setApproveDenyState] = useState<'' | `loading-${ComplianceStatus}` | 'error' | 'success'>('')
  const [lastClickedItem, setLastClickedItem] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saveError, setSaveError] = useState<boolean>(false)
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null)
  const [sewpBulkEditOpen, setSewpBulkEditOpen] = useState<boolean>(false)
  const [openTrHeader, setOpenTrHeader] = useState<sewpTrHeaderType | null>(null)

  const updatePropertyRef = useRef(updateProperty)
  updatePropertyRef.current = updateProperty
  const pendingSaves = useRef(new Map<string, (value: string) => void>())

  const debouncedUpdateProperty = (ids: string[], property: string, value: string) => {
    setSaveError(false)
    const updates: [string[], string, string][] = [[ids, property, value]]
    if (property === 'CatalogNumber') {
      for (const id of ids) {
        const row = catalogItems.find(item => item.id === id)
        if (!row || !('VendorPartNumber' in row)) continue
        const current = String(row.VendorPartNumber)
        const dash = current.lastIndexOf('-')
        updates.push([[id], 'VendorPartNumber', dash === -1 ? value : `${current.slice(0, dash + 1)}${value}`])
      }
    }
    for (const [updateIds, updateKey, updateValue] of updates) {
      const saveKey = [...updateIds].sort().join('\0') + updateKey
      setDrafts(prev => ({
        ...prev,
        ...Object.fromEntries(updateIds.map(id => [id + updateKey, updateValue])),
      }))
      let save = pendingSaves.current.get(saveKey)
      if (!save) {
        save = debounce((next: string) => {
          updatePropertyRef
            .current(updateIds, updateKey, next)
            .catch(err => {
              setSaveError(true)
              console.error('Failed to update property', err)
            })
            .finally(() =>
              setDrafts(prev => {
                const rest = { ...prev }
                for (const id of updateIds) {
                  if (rest[id + updateKey] === next) delete rest[id + updateKey]
                }
                return rest
              }),
            )
        }, 500)
        pendingSaves.current.set(saveKey, save)
      }
      save(updateValue)
    }
  }

  const hasLineApproval =
    contractNumber.toLowerCase() === 'gs-35f-0002v-olm' || contractNumber.toLowerCase() === 'gs-35f-0002v-open-market'
  const isSewp = contractNumber.toLowerCase() === 'nng15sd41b' || contractNumber.toLowerCase() === 'nng15sc86b'
  const isItes = contractNumber.toLowerCase() === 'w819tc25da023'
  const isOpenMarket = contractNumber.toLowerCase() === 'gs-35f-0002v-open-market'

  const activeColumns = visibleKeys.flatMap(k => {
    const col = contractColumns.find(c => c.key === k)
    return col ? [col] : []
  })

  const rightmostStickyKey = activeColumns.reduce<catalogColumnType | undefined>((edge, col) => {
    if (!col.sticky) return edge
    if (!edge?.sticky) return col
    return parseFloat(col.sticky.left) >= parseFloat(edge.sticky.left) ? col : edge
  }, undefined)?.key

  const stickyCellProps = (col: catalogColumnType) =>
    col.sticky
      ? {
          className: col.key === rightmostStickyKey ? 'sticky-cell sticky-edge' : 'sticky-cell',
          sx: { left: col.sticky.left },
        }
      : {}

  const manufacturerOptions = useMemo(() => {
    const present = new Set<string>()
    for (const item of catalogItems) {
      if ('manufacturer' in item && item.manufacturer) present.add(item.manufacturer)
    }
    return Array.from(present).sort((a, b) => a.localeCompare(b))
  }, [catalogItems])

  useEffect(() => {
    setTabGroup(tabGroups.get(contractNumber) ?? [])
  }, [contractNumber])

  useEffect(() => {
    setTableView(tabGroup[0] ?? '')
  }, [tabGroup])

  useEffect(() => {
    onTabChange(tableView)
    setSelectedIds([])
  }, [tableView])

  useEffect(() => {
    const newColumns =
      columnMapping.get(
        contractNumber.toLowerCase() === tableView.label.toLowerCase()
          ? contractNumber
          : `${contractNumber}:${tableView.label}`,
      ) ?? []
    setContractColumns(newColumns)
    const visible = newColumns.filter(c => !c.defaultHidden)
    setDefaultVisible(visible.map(c => c.key))
    setVisibleKeys(visible.map(c => c.key as keyof catalogItemType))
  }, [contractNumber, tableView])

  async function uploadFiles(_uploads: { file: File; modOptions: { modType?: string; modDate?: Dayjs } }[]) {
    await reload()
  }

  async function getFile(fileUrl: string, fileName: string) {
    if (!fileName) return
    try {
      const res = await axios.get(fileUrl, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download modification file', err)
    }
  }

  const selectRange = useCallback((allKeys: string[], anchor: string, current: string): string[] => {
    const anchorIndex = allKeys.indexOf(anchor)
    const currentIndex = allKeys.indexOf(current)
    if (anchorIndex === -1 || currentIndex === -1) return [current]
    const start = Math.min(anchorIndex, currentIndex)
    const end = Math.max(anchorIndex, currentIndex)
    return allKeys.slice(start, end + 1)
  }, [])

  const handleLineItemSelect = useCallback(
    (item: catalogTableItemListType[number], shiftKey: boolean) => {
      if (shiftKey && lastClickedItem && catalogItems.some(i => i.id === lastClickedItem)) {
        const rangeIds = selectRange(
          catalogItems.map(i => i.id),
          lastClickedItem,
          item.id,
        )
        setSelectedIds(rangeIds)
      } else {
        setSelectedIds(prev => (prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]))
        setLastClickedItem(item.id)
      }
    },
    [lastClickedItem, catalogItems],
  )

  const handleApproveDeny = (approvalStatus: ComplianceStatus) => {
    const items = catalogItems.flatMap(item =>
      item.kind === 'gsa' &&
      selectedIds.includes(item.id) &&
      item.oemCode &&
      item.mfrPartNumber &&
      item.sourceImpresQuoteNumber
        ? [
            {
              oemCode: item.oemCode,
              mfrPartNumber: item.mfrPartNumber,
              sourceImpresQuoteNumber: item.sourceImpresQuoteNumber,
            },
          ]
        : [],
    )
    setApproveDenyState(`loading-${approvalStatus}`)
    axios
      .put(`/catalog/${contractNumber}/modifications/approve`, { items, approvalStatus })
      .then(() => {
        reload()
        setApproveDenyState('success')
      })
      .catch(err => {
        console.error(err)
        setApproveDenyState('error')
      })
  }

  if (loading === 'error') {
    return (
      <ErrorScreen title="Error Loading Catalog" message="Please try again later" className="catalog-error-screen" />
    )
  }

  return (
    <div className="list-page catalog-page">
      <div className="list-table-container">
        <Divider />
        <Tabs
          value={tableView.label}
          onChange={(_, value) => setTableView(tabGroup.find(group => group.label === value) ?? tabGroup[0])}
          variant="fullWidth"
        >
          {tabGroup.map(group => (
            <Tab key={group.label} label={group.label} value={group.label} icon={group.icon} iconPosition="start" />
          ))}
        </Tabs>
        <Divider />
        <div className="list-table-pagination-header">
          <div className="list-table-pagination-header-left mfr-filter-container">
            <Autocomplete
              options={manufacturerOptions}
              value={manufacturerFilter}
              onChange={(_event, newValue) => onManufacturerFilterChange(newValue)}
              clearOnEscape
              size="small"
              renderInput={params => <TextField {...params} label="Manufacturer" />}
            />
            <TextField
              size="small"
              label="MFR Part No."
              value={mfrPartNumberFilter}
              onChange={e => onMfrPartNumberFilterChange(e.target.value)}
            />
            <Divider orientation="vertical" flexItem />
            {/*TODO: Wire button to export list to excel*/}
            <Button variant="outlined">Export to Excel</Button>
            {hasLineApproval && (
              <>
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  onClick={() => handleApproveDeny(ComplianceStatus.Compliant)}
                  loading={approveDenyState === `loading-${ComplianceStatus.Compliant}`}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Clear />}
                  onClick={() => handleApproveDeny(ComplianceStatus.NotCompliant)}
                  loading={approveDenyState === `loading-${ComplianceStatus.NotCompliant}`}
                >
                  Deny
                </Button>
              </>
            )}
            {isOpenMarket && (
              <>
                {/*Not Wired: Add to Catalog button subject to change*/}
                <Button variant="outlined">Add to Catalog</Button>
                <Button variant="outlined">Provide Quote CTA Ceiling Price</Button>
              </>
            )}
          </div>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
          />
        </div>

        <div className="catalog-mod-dropbox">
          {tableView.isModification && (
            <DragAndDropUpload onUploadComplete={uploadFiles} contractNumber={contractNumber} />
          )}
        </div>
        <TableContainer className="catalog-table-container">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {activeColumns.map(col => (
                  <TableCell key={col.key} width={col.width} align={col.align} {...stickyCellProps(col)}>
                    {col.key === 'check' ? (
                      <Checkbox
                        size="small"
                        checked={selectedIds.length === catalogItems.length && catalogItems.length > 0}
                        color="primary"
                        onChange={() => {
                          setSelectedIds(prev =>
                            prev.length === catalogItems.length ? [] : catalogItems.map(item => item.id),
                          )
                        }}
                      />
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading === 'loading'
                ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: activeColumns.length }).map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : catalogItems.map(row => (
                    <TableRow key={row.id} hover>
                      {activeColumns.map(col => {
                        const value = row[col.key as keyof catalogTableItemListType[number]] as
                          | string
                          | number
                          | boolean
                          | Date
                          | undefined
                        if (col.key === 'check') {
                          if (row.kind === 'gsa' && row.oemCode && row.mfrPartNumber && row.sourceImpresQuoteNumber) {
                            return (
                              <TableCell key={row.id + col.key}>
                                <Checkbox
                                  size="small"
                                  checked={selectedIds.includes(row.id)}
                                  onClick={e => handleLineItemSelect(row, e.shiftKey)}
                                />
                              </TableCell>
                            )
                          } else if (row.kind === 'sewpTr' || row.kind === 'itesAr') {
                            return (
                              <TableCell key={row.id + col.key} {...stickyCellProps(col)}>
                                <Checkbox
                                  className="sticky-cell"
                                  size="small"
                                  checked={selectedIds.includes(row.id)}
                                  onClick={e => handleLineItemSelect(row, e.shiftKey)}
                                />
                              </TableCell>
                            )
                          } else {
                            return <TableCell key={row.id + col.key} />
                          }
                        }
                        if (col.key === 'description' || col.key === 'ItemDescription') {
                          return (
                            <TableCell key={row.id + col.key}>
                              <Expandable key={row.id + col.key} text={String(value ?? '')} maxHeight={1} />
                            </TableCell>
                          )
                        }
                        if (col.key === 'status' && 'status' in row) {
                          if (isModification) {
                            const inProgress = row.status === 'inProgress' || row.status === 'processing'
                            return (
                              <TableCell key={row.id + col.key}>
                                {inProgress && (
                                  <Chip label="In Progress" size="small" color="warning" icon={<PendingOutlined />} />
                                )}
                                {row.status === 'failed' && (
                                  <Tooltip title={row.errorMessage}>
                                    <Chip label="Failed" size="small" color="error" icon={<Close />} />
                                  </Tooltip>
                                )}
                              </TableCell>
                            )
                          }
                        }
                        if (col.key === 'approvalStatus' && 'approvalStatus' in row) {
                          return (
                            <TableCell key={row.id + col.key}>
                              <Chip
                                label={
                                  row.approvalStatus === ComplianceStatus.PendingApproval
                                    ? 'Pending'
                                    : row.approvalStatus === ComplianceStatus.Compliant
                                    ? 'Approved'
                                    : 'Denied'
                                }
                                size="small"
                                color={row.approvalStatus === ComplianceStatus.PendingApproval ? 'warning' : 'error'}
                                icon={
                                  row.approvalStatus === ComplianceStatus.PendingApproval ? (
                                    <PendingOutlined />
                                  ) : (
                                    <Close />
                                  )
                                }
                              />
                            </TableCell>
                          )
                        }
                        if (col.key === 'TrNumber' && row.kind === 'sewpTrHeader') {
                          return (
                            <TableCell key={row.id + col.key}>
                              <MuiLink component="button" onClick={() => setOpenTrHeader(row)}>
                                {String(value)}
                              </MuiLink>
                            </TableCell>
                          )
                        }
                        if (col.key === 'TrTxt' && row.kind === 'sewpTrHeader') {
                          return (
                            <TableCell key={row.id + col.key}>
                              <Button
                                size="small"
                                onClick={() => getFile(`/catalog/${contractNumber}/tr/${row.RequestId}/file`, 'tr.txt')}
                              >
                                <FileDownloadOutlined /> tr.txt
                              </Button>
                            </TableCell>
                          )
                        }
                        if (col.key === 'SewpEmail' && row.kind === 'sewpTrHeader') {
                          return (
                            <TableCell key={row.id + col.key}>
                              {row.EmailFile && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    getFile(`/catalog/${contractNumber}/tr/${row.RequestId}/email`, row.EmailFile ?? '')
                                  }
                                >
                                  <FileDownloadOutlined /> {row.EmailFile}
                                </Button>
                              )}
                            </TableCell>
                          )
                        }
                        if (col.key === 'uploadedFile' && 'status' in row) {
                          const inProgress = row.status === 'inProgress' || row.status === 'processing'
                          return (
                            <TableCell key={row.id + col.key}>
                              <Button
                                size="small"
                                disabled={inProgress}
                                onClick={() =>
                                  getFile(`/catalog/${contractNumber}/modifications/${row.id}`, row.uploadedFile ?? '')
                                }
                              >
                                <FileDownloadOutlined /> {row.uploadedFile}
                              </Button>
                            </TableCell>
                          )
                        }
                        if (col.key === 'manufacturer' && 'oemCode' in row) {
                          return (
                            <TableCell key={row.id + col.key}>
                              <MuiLink
                                component={RouterLink}
                                to={`/suppliermanagement/manufacturer?mfr=${row.oemCode}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {String(value)}
                              </MuiLink>
                            </TableCell>
                          )
                        }
                        if (col.key === 'QuoteNumber' && 'QuoteId' in row) {
                          return (
                            <TableCell key={row.id + col.key}>
                              <MuiLink
                                component={RouterLink}
                                to={`/quote/${row.QuoteId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {String(value)}
                              </MuiLink>
                            </TableCell>
                          )
                        }
                        return (
                          <TableCell key={row.id + col.key} align={col.align} {...stickyCellProps(col)}>
                            {col.value ?? (
                              <EditableCell
                                value={row.id + col.key in drafts ? drafts[row.id + col.key] : value}
                                canEdit={col.editable ?? false}
                                onChange={value => {
                                  if (col.key === 'Manufacturer') {
                                    debouncedUpdateProperty([row.id], 'Manufacturer', value.Manufacturer)
                                  } else {
                                    debouncedUpdateProperty([row.id], col.key, value)
                                  }
                                }}
                                options={col.options}
                                inputType={col.inputType}
                                source={col.source}
                                contractNumber={contractNumber}
                              />
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {isSewp && (
          <SewpTrOverlay
            key={openTrHeader?.RequestId}
            contractNumber={contractNumber}
            trHeader={openTrHeader}
            onClose={() => setOpenTrHeader(null)}
            onSubmitted={() => {
              setOpenTrHeader(null)
              reload()
            }}
            onError={setErrorSnackbar}
          />
        )}
      </div>
      {(isSewp || isItes) && (
        <>
          <BulkEditActionBar
            contractNumber={contractNumber}
            tab={tableView.label}
            selectedItems={catalogItems.filter(
              (item): item is Extract<catalogTableItemListType[number], { kind: 'sewpTr' | 'itesAr' }> =>
                (item.kind === 'sewpTr' || item.kind === 'itesAr') && selectedIds.includes(item.id),
            )}
            reloadCatalog={reload}
            setBulkEditOpen={setSewpBulkEditOpen}
          />
          <BulkEdit
            open={sewpBulkEditOpen}
            contractNumber={contractNumber}
            selectedLineItems={catalogItems.filter(
              (item): item is Extract<catalogTableItemListType[number], { kind: 'sewpTr' | 'itesAr' }> =>
                (item.kind === 'sewpTr' || item.kind === 'itesAr') && selectedIds.includes(item.id),
            )}
            setLineItems={debouncedUpdateProperty}
            onClose={() => setSewpBulkEditOpen(false)}
          />
        </>
      )}
      <Snackbar
        open={approveDenyState === 'error'}
        onClose={() => {
          if (!approveDenyState.startsWith('loading-')) setApproveDenyState('')
        }}
      >
        <Alert severity="error" variant="filled">
          Failed to approve/deny quote items. Please try again.
        </Alert>
      </Snackbar>
      <Snackbar
        open={approveDenyState === 'success'}
        autoHideDuration={5000}
        onClose={() => {
          if (!approveDenyState.startsWith('loading-')) setApproveDenyState('')
        }}
        message="Quote items approved/denied successfully"
      />
      <Snackbar open={saveError} onClose={() => setSaveError(false)}>
        <Alert severity="error" variant="filled">
          Failed to save changes. Please try again.
        </Alert>
      </Snackbar>
      <Snackbar open={errorSnackbar !== null} onClose={() => setErrorSnackbar(null)}>
        <Alert severity="error" variant="filled">
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </div>
  )
}
